import {
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Typography,
  Checkbox,
  FormControlLabel,
  Link,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/logo_transparent_black.png";
import { acceptTerms, getTermsInfo, getTermsDocumentUrl, TermsInfo } from "../../api/terms";
import { useAuthStore } from "../../authProvider";

const TERMS_PDF_URL = getTermsDocumentUrl();

export function AcceptTerms(): JSX.Element {
  const navigate = useNavigate();
  const { setUser } = useAuthStore();

  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [termsInfo, setTermsInfo] = useState<TermsInfo | null>(null);
  const [loadingTermsInfo, setLoadingTermsInfo] = useState(true);

  // Fetch terms info on mount
  useEffect(() => {
    const fetchTermsInfo = async () => {
      try {
        const response = await getTermsInfo();
        if (response.data?.status === "SUCCESS" && response.data?.data) {
          setTermsInfo(response.data.data);
        } else {
          setError("Failed to load terms information");
        }
      } catch (err) {
        setError("Failed to load terms information");
      } finally {
        setLoadingTermsInfo(false);
      }
    };

    fetchTermsInfo();
  }, []);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    if (!accepted) {
      setError("You must accept the Terms and Conditions to continue");
      return;
    }

    if (!termsInfo) {
      setError("Terms information not available. Please refresh the page.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await acceptTerms({
        filename_hash: termsInfo.filename_hash,
        content_hash: termsInfo.content_hash,
      });

      if (response.data?.status === "SUCCESS" && response.data?.data?.user) {
        setUser(response.data.data.user);

        const returnUrl = new URLSearchParams(window.location.search).get(
          "returnUrl"
        );
        navigate(returnUrl || "/");
      } else {
        setError(response.data?.message || "Failed to accept terms");
      }
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(
        error.response?.data?.message ||
          "An error occurred while accepting terms"
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (loadingTermsInfo) {
    return (
      <Container maxWidth="md" sx={{ py: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "50vh",
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const clauseItems = [
    "Licenza d'uso non esclusiva e non trasferibile",
    "Limitazioni all'uso della piattaforma",
    "Obblighi del Cliente",
    "Responsabilità e manleva",
    "Limitazioni tecniche e API",
    "Gestione dei dati e responsabilità per la conservazione",
    "Esclusione di responsabilità per perdita dati",
    "Clausola sui contenuti generati dall'AI",
    "Clausola risolutiva espressa e sospensione per mancati pagamenti",
    "Proprietà intellettuale e tutela del know-how",
    "Trattamento dati secondo GDPR",
  ];

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <img
            src={logo}
            alt="Fashion Words Logo"
            style={{
              maxWidth: "150px",
              height: "auto",
              objectFit: "contain",
            }}
          />
        </Box>

        <Paper elevation={3} sx={{ p: 4, backgroundColor: "background.paper" }}>
          <Typography
            variant="h5"
            component="h1"
            sx={{
              textAlign: "center",
              fontWeight: "bold",
              mb: 1,
              color: "text.primary",
            }}
          >
            Termini e condizioni d'uso della piattaforma FashionAI® e MyContent
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              textAlign: "center",
              mb: 1,
              color: "text.secondary",
              fontStyle: "italic",
            }}
          >
            SaaS License Agreement
          </Typography>

          <Box
            sx={{
              maxHeight: "400px",
              overflowY: "auto",
              mb: 3,
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              backgroundColor: "grey.50",
            }}
          >
            <Typography variant="body1" sx={{ mb: 2, color: "text.primary" }}>
              Benvenuto su MyContent. L'utilizzo della piattaforma FashionAI® e
              MyContent è regolato dal Contratto di Licenza d'Uso SaaS
              ("Contratto") riportato integralmente al link sottostante.
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, color: "text.primary" }}>
              Accedendo o utilizzando la piattaforma, l'utente ("Cliente")
              accetta integralmente i termini e le condizioni del Contratto,
              inclusi:
            </Typography>

            <List dense sx={{ pl: 2, mb: 2 }}>
              {clauseItems.map((item, index) => (
                <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                  <ListItemText
                    primary={`• ${item}`}
                    primaryTypographyProps={{
                      variant: "body2",
                      color: "text.primary",
                    }}
                  />
                </ListItem>
              ))}
            </List>

            <Typography
              variant="body1"
              sx={{ mt: 3, mb: 2, color: "text.primary" }}
            >
              Per leggere il Contratto completo:
            </Typography>

            <Stack spacing={1} sx={{ pl: 2 }}>
              <Link
                href={TERMS_PDF_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                👉 Scarica il Contratto di Licenza d'Uso (PDF)
              </Link>
            </Stack>
          </Box>

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <Paper
                variant="outlined"
                sx={{ p: 2, backgroundColor: "grey.50" }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={accepted}
                      onChange={(e) => {
                        setAccepted(e.target.checked);
                        setError(null);
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.primary", mb: 1 }}
                      >
                        Accetto il Contratto di Licenza d'Uso della piattaforma
                        FashionAI® e MyContent, incluse le sue clausole su
                        responsabilità, limiti d'uso, perdita dati, proprietà
                        intellettuale e trattamento dei dati.{" "}
                        <Link
                          href={TERMS_PDF_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          [Leggi il contratto]
                        </Link>
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        I accept the SaaS License Agreement for the FashionAI®
                        and MyContent platforms, including all clauses on
                        liability, usage limits, data loss, intellectual
                        property and data processing.{" "}
                        <Link
                          href={TERMS_PDF_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          [Read the agreement]
                        </Link>
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start", m: 0 }}
                />
              </Paper>

              {error && (
                <Typography
                  color="error"
                  variant="body2"
                  sx={{
                    textAlign: "center",
                    p: 1,
                    borderRadius: 1,
                    backgroundColor: "error.light",
                    color: "error.contrastText",
                  }}
                >
                  {error}
                </Typography>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading || !accepted}
              >
                {isLoading ? "Processing..." : "Accept and Continue"}
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
